import { isQuickClarityInsight } from "./isQuickClarityInsight.js";
import { normalizeReflectionEmotion, DEFAULT_EMOTION_WHEN_MISSING } from "../constants/emotions.js";
import { canonicalCbtThinkingPattern, getCbtThinkingPatternMeta } from "../constants/cbtThinkingPatterns.js";
import { canonicalBehaviorPattern } from "../constants/behaviorPatterns.js";

const CONFIRMED_PATTERN_STATUSES = new Set(["yes", "partly"]);

function getInsightJson(insightOrRow) {
  if (!insightOrRow || typeof insightOrRow !== "object") return null;
  if (insightOrRow.insight_json && typeof insightOrRow.insight_json === "object") {
    return insightOrRow.insight_json;
  }
  return insightOrRow;
}

/**
 * Emotion label for Emotional Spread / longitudinal reporting.
 * Quick Clarity: only confirmed or corrected primary emotions count.
 * Legacy (non–Quick Clarity): keep AI sentiment so history still charts.
 * Returns null when the insight should be excluded from the spread.
 */
export function getReportingEmotionLabel(insightOrRow) {
  if (!insightOrRow || typeof insightOrRow !== "object") return null;
  const json = getInsightJson(insightOrRow);
  if (!json) return null;

  const confirmation = json.confirmation && typeof json.confirmation === "object" ? json.confirmation : null;
  const ep = confirmation?.emotion_primary;

  if (ep?.status === "removed") return null;

  if (ep?.status === "corrected") {
    const corrected = String(confirmation.emotion_primary_corrected || "").trim();
    if (corrected) return normalizeReflectionEmotion(corrected);
  }

  if (ep?.status === "confirmed") {
    const label =
      json.emotions?.primary?.label ||
      json.ai_suggestion?.emotions?.primary?.label ||
      json.sentiment ||
      insightOrRow.sentiment ||
      "";
    const raw = String(label).trim();
    if (!raw) return null;
    return normalizeReflectionEmotion(raw);
  }

  if (isQuickClarityInsight(json)) {
    // Unconfirmed Quick Clarity emotions stay out of the spread.
    return null;
  }

  const legacy = String(json.sentiment || insightOrRow.sentiment || "").trim();
  if (!legacy) return null;
  return normalizeReflectionEmotion(legacy || DEFAULT_EMOTION_WHEN_MISSING);
}

/**
 * Confirmed CBT thinking pattern (Quick Clarity) or legacy behavior_pattern.
 * Returns { kind: 'cbt'|'legacy', key, label } or null.
 */
export function getReportingThinkingPattern(insightOrRow) {
  const json = getInsightJson(insightOrRow);
  if (!json) return null;

  if (isQuickClarityInsight(json)) {
    const status = String(json.confirmation?.pattern_status || "").toLowerCase();
    if (!CONFIRMED_PATTERN_STATUSES.has(status)) return null;
    const raw =
      json.possible_cbt_pattern?.key ||
      json.possible_cbt_pattern?.label ||
      (typeof json.possible_cbt_pattern === "string" ? json.possible_cbt_pattern : "") ||
      json.ai_suggestion?.possible_cbt_pattern?.key ||
      "";
    const key = canonicalCbtThinkingPattern(raw);
    if (!key) return null;
    const meta = getCbtThinkingPatternMeta(key);
    return { kind: "cbt", key, label: meta?.label || key };
  }

  const legacy = canonicalBehaviorPattern(json.behavior_pattern);
  if (!legacy) return null;
  return { kind: "legacy", key: legacy, label: legacy };
}
